import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Map } from 'immutable';
import { connect } from 'react-redux';

import {
  showPopup,
  hidePopup,
  handleStartPopupLoading,
  handleEndPopupLoading,
  handleUpdatePopupFormData,
} from '../actions/popup';
import connectOptions, { mergeProps } from '../utils/connectOptions';
import Popup, { POPUPS } from './Popup';
import { PopupContext } from '../contexts/PopupContext';

const PopupWrapperContainer = props => {
  const {
    children,
    popup,
    showPopup,
    hidePopup,
    handleStartPopupLoading: startPopupLoadingAction,
    handleEndPopupLoading: endPopupLoadingAction,
    handleUpdatePopupFormData: updatePopupFormDataAction,
  } = props;

  const onCloseCallbacksRef = useRef([]);

  const isClosablePopup = useCallback(() => {
    const popupConfig = POPUPS[popup.get('name')];

    return popupConfig && popupConfig.closable !== false;
  }, [popup]);

  const openPopup = useCallback(
    (name, popupProps) => {
      showPopup({ name, props: popupProps });
      onCloseCallbacksRef.current = [];
    },
    [showPopup],
  );

  const closePopup = useCallback(() => {
    hidePopup();
    onCloseCallbacksRef.current.forEach(callback => callback());
    onCloseCallbacksRef.current = [];
  }, [hidePopup]);

  /**
   * Exposed to popup content as `safeClosePopup` (see `contextValue` below) - popup content
   * must use this, not the plain `closePopup` above, or it will bypass the `closable: false`
   * gate for that popup.
   */
  const safeClose = useCallback(
    (...rest) => {
      if (isClosablePopup()) {
        return closePopup(...rest);
      }

      return undefined;
    },
    [isClosablePopup, closePopup],
  );

  const onClosePopup = useCallback(callback => {
    onCloseCallbacksRef.current.push(callback);
  }, []);

  const hasPopup = useCallback(() => popup.get('name') !== null, [popup]);

  const getPopupName = useCallback(() => popup.get('name'), [popup]);

  const startPopupLoading = useCallback(() => {
    startPopupLoadingAction();
  }, [startPopupLoadingAction]);

  const endPopupLoading = useCallback(
    error => {
      endPopupLoadingAction(error);
      if (!error) {
        closePopup();
      }
    },
    [endPopupLoadingAction, closePopup],
  );

  const updatePopupFormData = useCallback(
    formData => {
      updatePopupFormDataAction(formData);
    },
    [updatePopupFormDataAction],
  );

  useEffect(() => {
    const handleEscapeKeyUp = event => {
      if (event.key === 'Escape' && isClosablePopup()) {
        closePopup();
      }
    };

    document.body.addEventListener('keyup', handleEscapeKeyUp);

    return () => {
      document.body.removeEventListener('keyup', handleEscapeKeyUp);
    };
  }, [isClosablePopup, closePopup]);

  const contextValue = useMemo(
    () => ({
      openPopup,
      onClosePopup,
      closePopup,
      safeClosePopup: safeClose,
      hasPopup,
      startPopupLoading,
      endPopupLoading,
      updatePopupFormData,
      isClosablePopup,
      getPopupName,
    }),
    [
      openPopup,
      onClosePopup,
      closePopup,
      safeClose,
      hasPopup,
      startPopupLoading,
      endPopupLoading,
      updatePopupFormData,
      isClosablePopup,
      getPopupName,
    ],
  );

  return (
    <PopupContext.Provider value={contextValue}>
      <div className="full-height">
        <Popup popup={popup} />
        {children}
      </div>
    </PopupContext.Provider>
  );
};

PopupWrapperContainer.propTypes = {
  children: PropTypes.node,
  handleEndPopupLoading: PropTypes.func.isRequired,
  handleStartPopupLoading: PropTypes.func.isRequired,
  handleUpdatePopupFormData: PropTypes.func.isRequired,
  hidePopup: PropTypes.func.isRequired,
  popup: PropTypes.instanceOf(Map).isRequired,
  showPopup: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    popup: state.popup,
  };
}

export { PopupWrapperContainer };
export default connect(
  mapStateToProps,
  {
    showPopup,
    hidePopup,
    handleStartPopupLoading,
    handleEndPopupLoading,
    handleUpdatePopupFormData,
  },
  mergeProps,
  connectOptions,
)(PopupWrapperContainer);
